import { motion } from "framer-motion";
import { Plus, Sparkles } from "lucide-react";

const RELATED_MARKETS = {
  sports_nfl: {
    keywords: [
      "super bowl",
      "nfl",
      "football",
      "chiefs",
      "eagles",
      "quarterback",
    ],
    suggestions: [
      "Who will win the Super Bowl MVP?",
      "Will the halftime show break viewership records?",
      "Over/under total points scored in the Super Bowl?",
    ],
  },
  sports_nba: {
    keywords: ["nba", "basketball", "lakers", "celtics", "lebron", "curry"],
    suggestions: [
      "Who will win the NBA MVP this season?",
      "Will LeBron average 25+ points this season?",
      "Which team will have the best regular season record?",
    ],
  },
  crypto_bitcoin: {
    keywords: ["bitcoin", "btc", "100k", "halving"],
    suggestions: [
      "Will Bitcoin hit a new all-time high this quarter?",
      "Will Bitcoin dominance stay above 50%?",
      "Will a Bitcoin ETF surpass $50B in AUM?",
    ],
  },
  crypto_ethereum: {
    keywords: ["ethereum", "eth", "solana", "sol"],
    suggestions: [
      "Will Ethereum flip Bitcoin in market cap?",
      "Will ETH reach $10K this cycle?",
      "Which L2 will have the most TVL by year-end?",
    ],
  },
  technology_ai: {
    keywords: [
      "gpt",
      "openai",
      "ai",
      "artificial intelligence",
      "claude",
      "gemini",
    ],
    suggestions: [
      "Will OpenAI release GPT-5 this year?",
      "Will an AI pass the bar exam with a perfect score?",
      "Which company will lead in AI revenue this year?",
    ],
  },
  politics_elections: {
    keywords: ["election", "president", "vote", "congress", "senate"],
    suggestions: [
      "Which party will win the next presidential election?",
      "Will voter turnout exceed 65%?",
      "Will a third-party candidate get more than 5%?",
    ],
  },
  finance_default: {
    keywords: ["stock", "fed", "interest", "market", "recession"],
    suggestions: [
      "Will the Fed cut rates this quarter?",
      "Will the S&P 500 reach a new all-time high?",
      "Will there be a recession this year?",
    ],
  },
};

const CATEGORY_FALLBACKS = {
  sports: [
    "Who will win the Super Bowl this year?",
    "Which team will win the NBA Finals?",
    "Will a record be broken at the next Olympics?",
  ],
  crypto: [
    "Will total crypto market cap hit $5T?",
    "Will a new top-10 cryptocurrency emerge?",
    "Will DeFi TVL exceed $200B?",
  ],
  technology: [
    "Will quantum computing achieve a major breakthrough?",
    "Which tech company will have the biggest IPO?",
    "Will self-driving cars be approved in 5 more states?",
  ],
  politics: [
    "Will a major trade deal be signed this year?",
    "Will NATO expand to new members?",
    "Will any country leave a major international org?",
  ],
  entertainment: [
    "Which streaming service will gain the most subscribers?",
    "Will a video game break launch-day sales records?",
    "Will any TV show surpass 100M viewers?",
  ],
  finance: [
    "Will the dollar index break 110?",
    "Will oil prices exceed $100/barrel?",
    "Will any major bank fail this year?",
  ],
};

export function getRelatedSuggestions(question, category) {
  const lowerQ = question.toLowerCase();

  for (const pool of Object.values(RELATED_MARKETS)) {
    if (pool.keywords.some((kw) => lowerQ.includes(kw))) {
      return pool.suggestions
        .filter((s) => s.toLowerCase() !== lowerQ)
        .slice(0, 3);
    }
  }

  const fallback = CATEGORY_FALLBACKS[category];
  if (fallback) return fallback.slice(0, 3);

  return [
    "Will a major world event happen this month?",
    "Which trend will dominate social media next?",
    "Will any company announce a surprise merger?",
  ];
}

export const RelatedMarketSuggestions = ({ category, question, onSelect }) => {
  const suggestions = getRelatedSuggestions(question, category);

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7 }}
      className="gm-related"
    >
      <div className="gm-related-header">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="22"
          height="22"
          viewBox="0 0 22 22"
          fill="none"
        >
          <path
            d="M3.66662 12.8334C3.49315 12.834 3.32308 12.7854 3.17616 12.6932C3.02924 12.601 2.9115 12.4689 2.83662 12.3125C2.76175 12.156 2.7328 11.9815 2.75316 11.8092C2.77352 11.6369 2.84233 11.474 2.95162 11.3393L12.0266 1.98928C12.0947 1.9107 12.1875 1.8576 12.2897 1.8387C12.3919 1.81979 12.4975 1.8362 12.5892 1.88524C12.6809 1.93427 12.7532 2.01301 12.7942 2.10854C12.8352 2.20407 12.8425 2.3107 12.815 2.41094L11.055 7.92928C11.0031 8.06817 10.9856 8.21759 11.0042 8.3647C11.0227 8.51181 11.0766 8.65223 11.1614 8.77392C11.2461 8.8956 11.3591 8.99491 11.4906 9.06334C11.6222 9.13176 11.7683 9.16726 11.9166 9.16678H18.3333C18.5068 9.16618 18.6768 9.21482 18.8237 9.30705C18.9707 9.39927 19.0884 9.53128 19.1633 9.68776C19.2382 9.84423 19.2671 10.0187 19.2467 10.191C19.2264 10.3633 19.1576 10.5262 19.0483 10.6609L9.97329 20.0109C9.90521 20.0895 9.81245 20.1426 9.71022 20.1615C9.60799 20.1804 9.50237 20.164 9.4107 20.115C9.31903 20.0659 9.24675 19.9872 9.20573 19.8917C9.16471 19.7961 9.15738 19.6895 9.18495 19.5893L10.945 14.0709C10.9968 13.932 11.0143 13.7826 10.9957 13.6355C10.9772 13.4884 10.9233 13.348 10.8385 13.2263C10.7538 13.1046 10.6408 13.0053 10.5093 12.9369C10.3777 12.8685 10.2316 12.833 10.0833 12.8334H3.66662Z"
            stroke="#E6FF00"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span className="gm-related-title">Related Markets</span>
      </div>
      <div className="gm-related-list">
        {suggestions.map((suggestion, i) => (
          <motion.button
            key={suggestion}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8 + i * 0.1 }}
            onClick={() => onSelect(suggestion)}
            className="gm-related-item"
          >
            <div className="gm-related-icon">
              <Plus className="gm-icon-xs gm-text-primary" />
            </div>
            <span className="gm-related-text">{suggestion}</span>
          </motion.button>
        ))}
      </div>
    </motion.div>
  );
};
